import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import type { CandidateComparison, ComparisonGroup, ComparisonRow, Position } from '@shared/types';
import { api } from '../api/client.js';
import { levelLabel } from '../pages/PersonCard.js';

/**
 * เทียบผู้เข้าเงื่อนไขที่ผู้ใช้ติ๊กไว้ เคียงกันไม่เกินสามคน
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ หน้านี้ไม่ตัดสินใครทั้งสิ้น
 * ────────────────────────────────────────────────────────────────
 *
 * ไม่มีคะแนนรวม ไม่มีอันดับ ไม่มีการทำตัวหนาให้ "คนที่ดีกว่า"
 * สิ่งเดียวที่หน้านี้เน้นคือ "แถวที่ค่าไม่เท่ากัน" ซึ่งเป็นเครื่องหมายว่าต่างกัน
 * ไม่ใช่เครื่องหมายว่าใครดีกว่า — เหตุผลเต็มอยู่ที่ CandidateComparison ใน types.ts
 *
 * ⚠ ห้ามเติมการไฮไลต์ "ค่าที่สูงที่สุดในแถว" ลงในหน้านี้ ไม่ว่าจะดูมีประโยชน์แค่ไหน
 *   ชั่วโมงอบรมที่มากกว่าหรือคะแนนประเมินที่สูงกว่า ไม่มีเอกสารของ ก.พ. ข้อไหน
 *   บอกว่าแปลว่ามีคุณสมบัติเหมาะสมกว่าในการแต่งตั้ง การทาสีคือการบอกว่ามี
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมหัวคอลัมน์ซ้ำทุกกลุ่ม
 * ────────────────────────────────────────────────────────────────
 *
 * ตารางนี้ยาวกว่าหน้าจอ ถ้ามีหัวคอลัมน์ชุดเดียวที่ด้านบน พอเลื่อนลงมาดูแถวล่าง ๆ
 * จะจำไม่ได้ว่าคอลัมน์กลางเป็นใคร แล้วอ่านค่าของคนผิดคนโดยไม่รู้ตัว
 *
 * ⚠ ทางแก้ที่ตรงกว่าคือ position: sticky ที่หัวคอลัมน์ แต่ใช้ที่นี่ไม่ได้
 *   หน้านี้ถูกวางในกล่องเลื่อนของหน้าต่าง (.rosbody) ซึ่งมีกล่องที่ overflow: hidden
 *   คั่นอยู่ในบางเส้นทาง sticky จะไปเกาะกล่องนั้นแทนขอบหน้าต่าง
 *   ซ้ำหัวคอลัมน์แบบนี้ได้ผลเหมือนกันและไม่ขึ้นกับโครงของกล่องที่ครอบอยู่
 */
export function CandidateCompare({
  pos,
  ids,
  onBack,
}: {
  pos: Position;
  ids: string[];
  onBack: () => void;
}) {
  const [diffOnly, setDiffOnly] = useState(false);
  const [warnOpen, setWarnOpen] = useState(false);

  /**
   * Esc ตอนอยู่หน้าเทียบ = ถอยกลับไปรายชื่อ ไม่ใช่ปิดทั้งหน้าต่าง
   *
   * ⚠ ต้องดักในช่วง capture แล้ว stopPropagation ไม่ใช่ดักแบบธรรมดา
   *   หน้าต่างที่ครอบอยู่ผูก keydown ไว้ที่ document ในช่วง bubble (PositionListPanel)
   *   ถ้าดักแบบธรรมดาทั้งสองตัวจะทำงานพร้อมกัน กด Esc ครั้งเดียวแล้วเด้งกลับสองชั้นรวด
   *   ผู้ใช้ที่แค่อยากเลิกเทียบจะหลุดออกไปถึงบัญชีรายชื่อทั้งที่กดปุ่มเดียว
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onBack();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onBack]);

  const q = useQuery({
    /* ⚠ ลำดับใน ids เป็นส่วนหนึ่งของคำตอบ (ลำดับคอลัมน์) จึงต้องอยู่ใน key ตามลำดับจริง
       ห้าม sort ก่อนทำ key ไม่งั้นสลับลำดับที่เลือกแล้วยังเห็นตารางเดิมที่แคชไว้ */
    queryKey: ['candidate-compare', pos.posId, ids.join(',')],
    queryFn: () => api.compareCandidates(pos.posId, ids),
  });

  return (
    <div className="cmp">
      <div className="cmp-top">
        <button type="button" className="cmp-back" onClick={onBack}>
          {/* ลูกศรวาดด้วย CSS ไม่ใช่ตัวอักษร — เหตุผลเดียวกับปุ่มกลับของหน้าต่าง (styles.css) */}
          <i aria-hidden="true" />
          กลับไปที่รายชื่อผู้เข้าเงื่อนไข
        </button>
        <h4>
          เทียบผู้เข้าเงื่อนไข
          <span className="cmp-seat">
            {pos.title} · {levelLabel(pos.level)}
            {pos.posNo && ` · เลขที่ ${pos.posNo}`}
          </span>
        </h4>
      </div>

      {q.isPending && <p className="state sm">กำลังรวบรวมข้อมูลของแต่ละคน…</p>}
      {q.isError && <p className="state sm error">{String(q.error)}</p>}
      {q.data && <Table d={q.data} diffOnly={diffOnly} onDiffOnly={setDiffOnly} warnOpen={warnOpen} onWarnOpen={setWarnOpen} />}
    </div>
  );
}

function Table({
  d,
  diffOnly,
  onDiffOnly,
  warnOpen,
  onWarnOpen,
}: {
  d: CandidateComparison;
  diffOnly: boolean;
  onDiffOnly: (v: boolean) => void;
  warnOpen: boolean;
  onWarnOpen: (v: boolean) => void;
}) {
  const n = d.people.length;
  /* จำนวนคอลัมน์ส่งเข้า CSS ทางตัวแปร ไม่ใช่เขียน grid-template-columns ใน JS
     ความกว้างของแต่ละคอลัมน์ต้องเปลี่ยนตาม breakpoint ซึ่งเป็นเรื่องของ CSS */
  const style = { '--cmp-n': n } as CSSProperties;
  const diffCount = d.groups.reduce((sum, g) => sum + g.rows.filter((r) => !r.same).length, 0);

  return (
    <div className="cmp-body" style={style}>
      <p className="cmp-lead">
        ตรวจกับ <b>{d.standardTitle ?? 'ยังไม่มีเอกสารมาตรฐานกำหนดตำแหน่งของระดับนี้ในระบบ'}</b>
      </p>

      {/* ⚠ ถ้อยคำนี้ต้องอยู่เหนือตารางเสมอ ไม่ใช่ซ่อนไว้ในกล่องข้อจำกัดด้านล่าง
          ตารางเทียบสามคอลัมน์อ่านเหมือนใบให้คะแนนโดยธรรมชาติ ต้องบอกตั้งแต่ก่อนอ่าน
          ว่ามันไม่ใช่ใบให้คะแนน ไม่ใช่บอกทีหลังตอนอ่านจบแล้ว */}
      <p className="cmp-guard">
        หน้านี้วางข้อเท็จจริงของแต่ละคนเรียงกันเท่านั้น <b>ไม่ได้จัดอันดับและไม่ได้ชี้ว่าใครเหมาะสมกว่า</b>{' '}
        แถวที่ระบายไว้คือแถวที่ค่าไม่เท่ากัน เป็นเครื่องหมายว่า “ต่างกัน” ไม่ใช่ “ดีกว่า”
      </p>

      {d.missingIds.length > 0 && (
        <p className="cmp-missing">
          ไม่พบข้อมูลของ {d.missingIds.join(', ')} จึงไม่ได้อยู่ในตารางนี้
        </p>
      )}

      <div className="cmp-tools">
        <label className="cmp-diff">
          <input type="checkbox" checked={diffOnly} onChange={(e) => onDiffOnly(e.target.checked)} />
          แสดงเฉพาะแถวที่ค่าต่างกัน
          <span className="cmp-diffn">{diffCount} จาก {d.groups.reduce((s, g) => s + g.rows.length, 0)} แถว</span>
        </label>
      </div>

      {d.groups.map((g) => (
        <Group key={g.key} g={g} people={d.people} diffOnly={diffOnly} />
      ))}

      <Forecast people={d.people} seat={d.title} />

      {/* ---------- ข้อจำกัด ----------
          ⚠ ยุบไว้ได้ แต่ห้ามตัดออก และห้ามยุบข้อความบรรทัด cmp-guard ด้านบนลงมารวมที่นี่
            สองอย่างนี้ทำหน้าที่ต่างกัน — บนคือกันอ่านผิดตั้งแต่แรก ล่างคือรายละเอียดของข้อจำกัด */}
      <section className={`pmodal-sec vc-sec${warnOpen ? ' is-open' : ''}`}>
        <h4>
          <button
            type="button"
            className="vc-secbtn"
            aria-expanded={warnOpen}
            onClick={() => onWarnOpen(!warnOpen)}
          >
            <span className="vc-caret" aria-hidden="true">
              {warnOpen ? <DownOutlined /> : <RightOutlined />}
            </span>
            ข้อจำกัดของการเทียบนี้
            <span className="vc-count">{d.warnings.length} ข้อ</span>
          </button>
        </h4>
        {warnOpen && (
          <ul className="pmodal-warn">
            {d.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * ปุ่ม "คาดการณ์ถ้ารับตำแหน่งนี้" ใต้คอลัมน์ของแต่ละคน
 *
 * ผู้ใช้สั่ง 20 ก.ย. 2569: เพิ่มปุ่มคาดการณ์ว่าถ้าคนนี้มารับตำแหน่งจะเกิดอะไรขึ้น ดี/ไม่ดีอย่างไร
 * "แต่ไม่ต้องวิเคราะห์จริง" — ตอนนี้กดแล้วขึ้นว่าข้อมูลไม่เพียงพอเท่านั้น
 * ⚠ ห้ามเติมคำคาดการณ์ใด ๆ ลงไปเองจนกว่าจะมีข้อมูลรองรับ (ภาระงาน ผลงานรายตำแหน่ง ผลกระทบต่อหน่วยต้นทาง)
 *   ข้อความเดาบนหน้าเทียบจะถูกอ่านเป็นความเห็นของระบบว่าใครเหมาะสมกว่า
 */
function Forecast({ people, seat }: { people: CandidateComparison['people']; seat: string }) {
  /* ผู้ใช้สั่ง 20 ก.ย. 2569 รอบสอง: เปลี่ยนข้อความปุ่ม และแจ้งผลเป็นหน้าต่าง (modal) ด้วยถ้อยคำทางการ */
  const [who, setWho] = useState<CandidateComparison['people'][number] | null>(null);
  return (
    <div className="cmp-grid cmp-fcgrid" role="table">
      <div className="cmp-row cmp-fcrow" role="row">
        <div className="cmp-lbl" role="rowheader">
          <b>การประเมินผลกระทบ</b>
          <span className="cmp-hint">ผลที่อาจเกิดขึ้นหากได้รับแต่งตั้งให้ดำรงตำแหน่งนี้</span>
        </div>
        {people.map((p) => (
          <div key={p.personaId} className="cmp-cell" role="cell">
            <button type="button" className="cmp-fcbtn" onClick={() => setWho(p)}>
              <span aria-hidden="true">◈</span> ประเมินผลกระทบหากดำรงตำแหน่งนี้
              <span className="sr-only"> — {p.name}</span>
            </button>
          </div>
        ))}
      </div>
      {who && <ForecastNotice name={who.name} seat={seat} onClose={() => setWho(null)} />}
    </div>
  );
}

/**
 * หน้าต่างแจ้งว่าข้อมูลไม่เพียงพอ
 * ⚠ Esc ต้องปิดแค่หน้าต่างนี้ ไม่ใช่เด้งออกจากหน้าเทียบด้วย — หน้าเทียบดัก Esc ที่ document ช่วง capture
 *   จึงดักที่ window ช่วง capture (ทำงานก่อน) แล้ว stopImmediatePropagation
 */
function ForecastNotice({ name, seat, onClose }: { name: string; seat: string; onClose: () => void }) {
  const okRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    okRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      prev?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <div className="fcm-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="fcm" role="alertdialog" aria-modal="true" aria-labelledby="fcm-t" aria-describedby="fcm-d">
        <div className="fcm-ico" aria-hidden="true">i</div>
        <h3 id="fcm-t">ไม่สามารถประเมินผลกระทบได้ในขณะนี้</h3>
        <p id="fcm-d">
          ข้อมูลที่ใช้ในการวิเคราะห์และคาดการณ์ผลกระทบ กรณี <b>{name}</b> ได้รับแต่งตั้งให้ดำรงตำแหน่ง
          <b> {seat}</b> ยังไม่เพียงพอ ระบบจึงไม่สามารถประมวลผลได้
        </p>
        <div className="fcm-need">
          <span>ข้อมูลที่จำเป็นต้องมีเพิ่มเติม</span>
          <ul>
            <li>ภาระงานและตัวชี้วัดของตำแหน่ง</li>
            <li>ผลการปฏิบัติงานรายตำแหน่งย้อนหลัง</li>
            <li>ผลกระทบต่อหน่วยงานต้นสังกัดเดิม</li>
          </ul>
        </div>
        <div className="fcm-foot">
          <button ref={okRef} type="button" className="fcm-ok" onClick={onClose}>
            รับทราบ
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Group({
  g,
  people,
  diffOnly,
}: {
  g: ComparisonGroup;
  people: CandidateComparison['people'];
  diffOnly: boolean;
}) {
  const [open, setOpen] = useState(true);
  const rows = diffOnly ? g.rows.filter((r) => !r.same) : g.rows;

  return (
    <section className={`cmp-grp${open ? ' is-open' : ''}`}>
      <h5>
        <button type="button" className="cmp-grpbtn" aria-expanded={open} onClick={() => setOpen(!open)}>
          <span className="cmp-caret" aria-hidden="true">
            {open ? <DownOutlined /> : <RightOutlined />}
          </span>
          {g.title}
          <span className="cmp-grpn">{g.rows.length} แถว</span>
        </button>
      </h5>
      <p className="cmp-grpnote">{g.note}</p>

      {open && (
        <div className="cmp-grid" role="table">
          <Heads people={people} />
          {rows.length === 0 ? (
            <p className="cmp-none">ทุกแถวในกลุ่มนี้มีค่าเท่ากันทุกคน</p>
          ) : (
            rows.map((r) => <Row key={r.key} r={r} people={people} />)
          )}
        </div>
      )}
    </section>
  );
}

/**
 * หัวคอลัมน์ — ชื่อคนพร้อมตำแหน่งและระดับปัจจุบัน
 *
 * ⚠ ต้องมี "ระดับ" คู่ชื่อตำแหน่งเสมอ ด้วยเหตุผลเดียวกับที่หัว VacancyCandidates.tsx
 *   ฟิลด์ title ในชุดข้อมูลมักเป็นชื่อสายงาน ไม่ใช่ชื่อตำแหน่งบริหารที่ครองอยู่จริง
 *
 * ⚠ ห้ามใส่เลขลำดับหน้าชื่อในหัวคอลัมน์ ลำดับนี้คือลำดับที่ผู้ใช้ติ๊ก
 *   ตัวเลข 1-2-3 บนหัวคอลัมน์จะถูกอ่านเป็นอันดับทันที
 */
function Heads({ people }: { people: CandidateComparison['people'] }) {
  return (
    <div className="cmp-row is-head" role="row">
      <div className="cmp-lbl" role="columnheader" />
      {people.map((p) => (
        <div key={p.personaId} className="cmp-cell is-head" role="columnheader">
          <b>{p.name}</b>
          <span className="cmp-sub">
            {p.currentTitle} · {levelLabel(p.currentLevel)}
          </span>
          <span className="cmp-sub">{p.unit}</span>
          {/* ⚠ ต้องบอกตรงหัวคอลัมน์ ไม่ใช่บอกในแถวเดียวข้างล่าง
              คนที่ไม่อยู่ในรายชื่อจะมีช่องว่างเต็มไปหมดทั้งคอลัมน์
              ถ้าไม่บอกไว้ที่หัว ผู้ใช้จะอ่านว่าเขา "ข้อมูลไม่ครบ" แทนที่จะเป็น "ระบบไม่ได้ตรวจให้" */}
          {p.inEligibleList ? (
            p.seniorityRank !== null && (
              <span className="cmp-chip">อาวุโสลำดับที่ {p.seniorityRank.toLocaleString('th-TH')}</span>
            )
          ) : (
            <span className="cmp-chip is-warn">ไม่อยู่ในรายชื่อที่ระบบตรวจให้สำหรับอัตรานี้</span>
          )}
        </div>
      ))}
    </div>
  );
}

function Row({ r, people }: { r: ComparisonRow; people: CandidateComparison['people'] }) {
  return (
    <div className={`cmp-row${r.same ? '' : ' is-diff'}`} role="row">
      <div className="cmp-lbl" role="rowheader">
        <b>{r.label}</b>
        {r.hint && <span className="cmp-hint">{r.hint}</span>}
      </div>
      {r.values.map((v, i) => (
        <div
          key={people[i]?.personaId ?? i}
          className={`cmp-cell is-${v.tone}${v.missing ? ' is-missing' : ''}`}
          role="cell"
        >
          {/* ชื่อคนซ้ำในช่อง — ซ่อนบนจอกว้าง โผล่เฉพาะตอนตารางพับเป็นแนวตั้งบนจอแคบ
              ไม่งั้นบนมือถือจะเห็นค่าสามค่าเรียงกันโดยไม่รู้ว่าค่าไหนของใคร */}
          <i className="cmp-who" aria-hidden="true">
            {people[i]?.name}
          </i>
          <b>{v.text}</b>
          {v.detail && <span className="cmp-detail">{v.detail}</span>}
          {v.items.length > 0 && (
            <ul className="cmp-items">
              {v.items.map((it, k) => (
                <li key={`${it}-${k}`}>{it}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
